import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import { submitRsvp } from "../lib/api";
import {
  Check,
  X,
  Plus,
  Trash2,
  ArrowLeft,
  ArrowRight,
  Loader2,
  Heart,
  HeartCrack,
  BedDouble,
  Music2,
  User,
  Baby,
} from "lucide-react";

// Inline custom SVG icons matching the watercolor wedding aesthetic.
// Always render colored — colors stay visible on any background so the icon
// remains recognizable.
function CheeseIcon({ size = 20 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {/* Top obtuse triangle (rind) — apex around 118° */}
      <path
        d="M2 11 L12 5 L22 11 Z"
        fill="#D49A4A"
        stroke="#5A3614"
        strokeWidth="1"
      />
      {/* Bottom rectangle (body) — attached to the longest side */}
      <rect
        x="2"
        y="11"
        width="20"
        height="8"
        fill="#F4C26A"
        stroke="#5A3614"
        strokeWidth="1"
      />
      {/* 2 holes in the triangle */}
      <circle cx="9.5" cy="9.5" r="0.7" fill="#5A3614" />
      <circle cx="14.5" cy="9.7" r="0.6" fill="#5A3614" />
      {/* 5 holes in the rectangle */}
      <circle cx="6" cy="14" r="0.9" fill="#5A3614" />
      <circle cx="12" cy="13.6" r="1" fill="#5A3614" />
      <circle cx="18" cy="14" r="0.85" fill="#5A3614" />
      <circle cx="8.5" cy="17" r="0.8" fill="#5A3614" />
      <circle cx="16" cy="17.2" r="0.85" fill="#5A3614" />
      {/* Subtle highlight along the left side of the triangle */}
      <path
        d="M3.5 11 L11.5 6.2"
        stroke="#FBE2A8"
        strokeWidth="0.6"
        opacity="0.7"
      />
    </svg>
  );
}

function TomatoIcon({ size = 20 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {/* Round tomato body — bright tomato red (visible on light AND burgundy) */}
      <circle
        cx="12"
        cy="14.5"
        r="6.6"
        fill="#E04C58"
        stroke="#6E1B25"
        strokeWidth="1.1"
      />
      {/* Soft inner highlight for fruit look */}
      <path
        d="M8.4 12 a 4 4 0 0 1 3 -2.5"
        stroke="#FBC4C4"
        strokeWidth="0.9"
        opacity="0.85"
      />
      {/* Star-shaped sage-green leafy crown on top */}
      <path
        d="M12 8 V 5"
        stroke="#5A6E20"
        strokeWidth="1.1"
      />
      <path
        d="M12 8 L 8 6.4 C 7.7 7.5 8.6 8.3 10 8.5 Z"
        fill="#86A03A"
        stroke="#3E5018"
        strokeWidth="0.7"
      />
      <path
        d="M12 8 L 16 6.4 C 16.3 7.5 15.4 8.3 14 8.5 Z"
        fill="#86A03A"
        stroke="#3E5018"
        strokeWidth="0.7"
      />
      <path
        d="M12 8 L 9.6 5.4 C 10.2 4.6 11.2 4.6 11.8 5.4 Z"
        fill="#A0BC4C"
        stroke="#3E5018"
        strokeWidth="0.6"
      />
      <path
        d="M12 8 L 14.4 5.4 C 13.8 4.6 12.8 4.6 12.2 5.4 Z"
        fill="#A0BC4C"
        stroke="#3E5018"
        strokeWidth="0.6"
      />
    </svg>
  );
}

const TOTAL_STEPS_ATTENDING = 5; // 0 attending, 1 adults, 2 children, 3 accomm+song, 4 contact
const TOTAL_STEPS_DECLINE = 2; // 0 attending, 1 contact

const emptyPerson = () => ({
  first_name: "",
  last_name: "",
  menu: "vegetarian",
  allergies: "",
});

const defaultForm = {
  attending: null,
  needs_accommodation: null,
  adults: [emptyPerson()],
  children: [],
  favorite_song: "",
  email: "",
  phone: "",
};

export default function RSVPForm() {
  const { t, lang } = useLang();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const setPerson = (kind, idx, key, value) => {
    setForm((f) => {
      const list = [...f[kind]];
      list[idx] = { ...list[idx], [key]: value };
      return { ...f, [kind]: list };
    });
  };

  const addPerson = (kind) =>
    setForm((f) => ({ ...f, [kind]: [...f[kind], emptyPerson()] }));

  const removePerson = (kind, idx) =>
    setForm((f) => ({
      ...f,
      [kind]: f[kind].filter((_, i) => i !== idx),
    }));

  const effectiveTotal =
    form.attending === false ? TOTAL_STEPS_DECLINE : TOTAL_STEPS_ATTENDING;

  const canProceed = () => {
    setError("");
    if (step === 0 && form.attending === null) {
      setError(t.rsvp.errors.required);
      return false;
    }

    // Helper: at least one of email/phone is provided; if email is provided, must be valid
    const validateContact = () => {
      const hasEmail = form.email.trim().length > 0;
      const hasPhone = form.phone.trim().length > 0;
      if (!hasEmail && !hasPhone) {
        setError(t.rsvp.errors.emailOrPhone);
        return false;
      }
      if (hasEmail && !/^[^@]+@[^@]+\.[^@]+$/.test(form.email.trim())) {
        setError(t.rsvp.errors.email);
        return false;
      }
      return true;
    };

    // Decline path
    if (form.attending === false) {
      if (step === 1) {
        return validateContact();
      }
      return true;
    }
    // Attending path
    if (step === 1) {
      const valid = form.adults.some(
        (a) => a.first_name.trim() && a.last_name.trim()
      );
      if (!valid) {
        setError(t.rsvp.errors.needName);
        return false;
      }
    }
    if (step === 4) {
      return validateContact();
    }
    return true;
  };

  const isLastStep =
    (form.attending === true && step === TOTAL_STEPS_ATTENDING - 1) ||
    (form.attending === false && step === TOTAL_STEPS_DECLINE - 1);

  const next = () => {
    if (!canProceed()) return;
    if (isLastStep) {
      submit();
    } else {
      setStep((s) => s + 1);
    }
  };

  const back = () => {
    setError("");
    setStep((s) => Math.max(0, s - 1));
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const filterPeople = (list) =>
        list
          .filter((p) => p.first_name.trim() && p.last_name.trim())
          .map((p) => ({
            first_name: p.first_name.trim(),
            last_name: p.last_name.trim(),
            menu: p.menu || "vegetarian",
            allergies: (p.allergies || "").trim(),
          }));
      const payload = {
        attending: !!form.attending,
        needs_accommodation: !!form.needs_accommodation,
        adults: form.attending ? filterPeople(form.adults) : [],
        children: form.attending ? filterPeople(form.children) : [],
        favorite_song: form.favorite_song,
        email: form.email,
        phone: form.phone,
        language: lang,
      };
      await submitRsvp(payload);
      setSuccess(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || t.rsvp.errors.generic;
      setError(typeof msg === "string" ? msg : t.rsvp.errors.generic);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(defaultForm);
    setStep(0);
    setSuccess(false);
    setError("");
  };

  return (
    <section
      id="rsvp"
      className="relative py-24 md:py-40 px-6 md:px-12 overflow-hidden"
      data-testid="rsvp-section"
    >
      <div
        className="brush-blob bg-burgundy/40"
        style={{ width: 380, height: 380, top: "20%", right: "-120px" }}
      />
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-14">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9 }}
            className="text-xs uppercase tracking-[0.4em] text-burgundy mb-4"
          >
            {t.rsvp.overline}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.1, delay: 0.1 }}
            className="font-serif font-light text-4xl md:text-6xl text-ink mb-4"
          >
            {t.rsvp.title}
          </motion.h2>
          <p className="font-serif italic text-ink/70 max-w-xl mx-auto">
            {t.rsvp.subtitle}
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 1.1 }}
          className="relative bg-ivory border border-wedding-border p-8 md:p-14 shadow-[0_20px_40px_rgba(26,21,20,0.08)]"
          data-testid="rsvp-form-card"
        >
          {/* Progress */}
          {!success && (
            <div className="flex items-center justify-between mb-10">
              <span className="text-xs uppercase tracking-[0.3em] text-warm-brown">
                {t.rsvp.step} {Math.min(step + 1, effectiveTotal)} {t.rsvp.of}{" "}
                {effectiveTotal}
              </span>
              <div className="flex-1 mx-4 h-[1px] bg-wedding-border relative">
                <motion.div
                  className="absolute inset-y-0 left-0 bg-burgundy"
                  initial={false}
                  animate={{
                    width: `${((step + 1) / effectiveTotal) * 100}%`,
                  }}
                  transition={{ duration: 0.6 }}
                />
              </div>
            </div>
          )}

          <AnimatePresence mode="wait">
            {success ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center py-10"
                data-testid="rsvp-success"
              >
                <Heart
                  size={48}
                  className="mx-auto text-burgundy mb-6"
                  fill="#7B1F2A"
                />
                <h3 className="font-serif font-light text-3xl md:text-4xl text-ink mb-4">
                  {t.rsvp.success.title}
                </h3>
                <p className="font-serif italic text-ink/70 max-w-md mx-auto mb-8">
                  {t.rsvp.success.body}
                </p>
                <button
                  onClick={reset}
                  className="px-8 py-3 border border-burgundy text-burgundy rounded-full text-xs uppercase tracking-[0.3em] hover:bg-burgundy hover:text-ivory transition-colors"
                  data-testid="rsvp-reset-button"
                >
                  {t.rsvp.success.again}
                </button>
              </motion.div>
            ) : (
              <motion.div
                key={`step-${step}-${form.attending}`}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.4 }}
              >
                {/* Step 0: attending */}
                {step === 0 && (
                  <div className="flex flex-col items-center text-center gap-8">
                    <h3 className="font-serif font-light text-2xl md:text-3xl text-ink">
                      {t.rsvp.attending.title}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-md">
                      <ChoiceCard
                        selected={form.attending === true}
                        onClick={() => update({ attending: true })}
                        icon={<Heart size={24} />}
                        label={t.rsvp.attending.yes}
                        testid="rsvp-attending-yes"
                      />
                      <ChoiceCard
                        selected={form.attending === false}
                        onClick={() => update({ attending: false })}
                        icon={<HeartCrack size={24} />}
                        label={t.rsvp.attending.no}
                        testid="rsvp-attending-no"
                      />
                    </div>
                  </div>
                )}

                {/* Decline contact */}
                {form.attending === false && step === 1 && (
                  <ContactStep
                    form={form}
                    update={update}
                    t={t}
                    testidPrefix="rsvp-decline-contact"
                  />
                )}

                {/* Step 1 (attending): adults with menu+allergies */}
                {form.attending === true && step === 1 && (
                  <PeopleStep
                    title={t.rsvp.adults.title}
                    subtitle={t.rsvp.adults.subtitle}
                    kind="adults"
                    Icon={User}
                    people={form.adults}
                    setPerson={setPerson}
                    addPerson={addPerson}
                    removePerson={removePerson}
                    t={t}
                    testidPrefix="rsvp-adults"
                  />
                )}

                {/* Step 2 (attending): children with menu+allergies */}
                {form.attending === true && step === 2 && (
                  <PeopleStep
                    title={t.rsvp.children.title}
                    subtitle={t.rsvp.children.subtitle}
                    kind="children"
                    Icon={Baby}
                    people={form.children}
                    setPerson={setPerson}
                    addPerson={addPerson}
                    removePerson={removePerson}
                    t={t}
                    testidPrefix="rsvp-children"
                    optional
                  />
                )}

                {/* Step 3: accommodation + song */}
                {form.attending === true && step === 3 && (
                  <div className="space-y-10">
                    <div>
                      <h3 className="font-serif font-light text-xl md:text-2xl text-ink mb-4 text-center">
                        {t.rsvp.accommodation.title}
                      </h3>
                      <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
                        <ChoiceCard
                          selected={form.needs_accommodation === true}
                          onClick={() => update({ needs_accommodation: true })}
                          icon={<BedDouble size={20} />}
                          label={t.rsvp.accommodation.yes}
                          compact
                          testid="rsvp-accommodation-yes"
                        />
                        <ChoiceCard
                          selected={form.needs_accommodation === false}
                          onClick={() => update({ needs_accommodation: false })}
                          icon={<X size={20} />}
                          label={t.rsvp.accommodation.no}
                          compact
                          testid="rsvp-accommodation-no"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-center gap-3 mb-2 text-burgundy">
                        <Music2 size={26} strokeWidth={1.4} />
                        <h3 className="font-serif font-light text-xl md:text-2xl text-ink">
                          {t.rsvp.song.title}
                        </h3>
                        <Music2 size={26} strokeWidth={1.4} className="-scale-x-100" />
                      </div>
                      <p className="font-serif italic text-ink/60 text-center mb-4">
                        {t.rsvp.song.subtitle}
                      </p>
                      <textarea
                        rows={2}
                        className="underline-input resize-none leading-relaxed"
                        placeholder={t.rsvp.song.placeholder}
                        value={form.favorite_song}
                        onChange={(e) =>
                          update({ favorite_song: e.target.value })
                        }
                        data-testid="rsvp-song-input"
                      />
                    </div>
                  </div>
                )}

                {/* Step 4: contact */}
                {form.attending === true && step === 4 && (
                  <ContactStep
                    form={form}
                    update={update}
                    t={t}
                    testidPrefix="rsvp-contact"
                  />
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error */}
          {error && !success && (
            <p
              className="mt-6 text-sm text-burgundy text-center font-serif italic"
              data-testid="rsvp-error"
            >
              {error}
            </p>
          )}

          {/* Nav */}
          {!success && (
            <div className="flex justify-between items-center mt-10 pt-6 border-t border-wedding-border">
              <button
                onClick={back}
                disabled={step === 0}
                className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.3em] text-ink/60 hover:text-burgundy transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="rsvp-back-button"
              >
                <ArrowLeft size={14} />
                {t.rsvp.back}
              </button>
              <button
                onClick={next}
                disabled={submitting}
                className="inline-flex items-center gap-3 px-8 py-3 bg-burgundy text-ivory rounded-full text-xs uppercase tracking-[0.3em] hover:bg-wine transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="rsvp-next-button"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    {t.rsvp.submitting}
                  </>
                ) : isLastStep ? (
                  <>
                    {t.rsvp.submit}
                    <Heart size={14} fill="currentColor" />
                  </>
                ) : (
                  <>
                    {t.rsvp.next}
                    <ArrowRight size={14} />
                  </>
                )}
              </button>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}

function ChoiceCard({ selected, onClick, icon, label, testid, compact }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-center justify-center gap-2 ${
        compact ? "p-4" : "p-6"
      } border rounded-sm transition-all duration-300 ${
        selected
          ? "border-burgundy bg-burgundy/5 text-burgundy shadow-[0_0_0_1px_#7B1F2A]"
          : "border-wedding-border text-ink/70 hover:border-burgundy/60 hover:text-burgundy"
      }`}
      data-testid={testid}
    >
      {icon}
      <span className="font-serif text-base md:text-lg">{label}</span>
    </button>
  );
}

function PersonCard({
  index,
  person,
  kind,
  Icon,
  setPerson,
  removePerson,
  t,
  testidPrefix,
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.4 }}
      className="relative border border-wedding-border bg-cream/30 p-5 md:p-7 rounded-sm"
      data-testid={`${testidPrefix}-card-${index}`}
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3 text-burgundy">
          <Icon size={16} />
          <span className="text-xs uppercase tracking-[0.3em]">
            {kind === "adults" ? t.rsvp.adults.person : t.rsvp.children.person}{" "}
            {index + 1}
          </span>
        </div>
        <button
          type="button"
          onClick={() => removePerson(kind, index)}
          className="text-ink/40 hover:text-burgundy transition-colors p-2 -m-2"
          data-testid={`${testidPrefix}-remove-${index}`}
          aria-label={t.rsvp.adults.remove}
        >
          <Trash2 size={14} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <input
          type="text"
          className="underline-input"
          placeholder={t.rsvp.adults.firstName}
          value={person.first_name}
          onChange={(e) =>
            setPerson(kind, index, "first_name", e.target.value)
          }
          data-testid={`${testidPrefix}-firstname-${index}`}
        />
        <input
          type="text"
          className="underline-input"
          placeholder={t.rsvp.adults.lastName}
          value={person.last_name}
          onChange={(e) => setPerson(kind, index, "last_name", e.target.value)}
          data-testid={`${testidPrefix}-lastname-${index}`}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 mb-5">
        {["vegetarian", "vegan"].map((m) => {
          const Icon = m === "vegetarian" ? CheeseIcon : TomatoIcon;
          return (
            <button
              key={m}
              type="button"
              onClick={() => setPerson(kind, index, "menu", m)}
              className={`py-3 px-2 sm:px-4 text-[11px] sm:text-xs uppercase tracking-[0.1em] sm:tracking-[0.25em] border rounded-2xl transition-all duration-300 whitespace-nowrap ${
                person.menu === m
                  ? "border-burgundy bg-burgundy text-ivory"
                  : "border-wedding-border text-ink/70 hover:border-burgundy hover:text-burgundy"
              }`}
              data-testid={`${testidPrefix}-menu-${m}-${index}`}
            >
              <span className="flex flex-col items-center justify-center gap-1.5">
                <Icon size={28} />
                <span className="inline-block mr-[-0.1em] sm:mr-[-0.25em]">
                  {t.rsvp.menu[m]}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <div>
        <label className="text-[10px] uppercase tracking-[0.3em] text-warm-brown mb-1 block">
          {t.rsvp.allergies.label}
        </label>
        <textarea
          rows={2}
          className="underline-input resize-none leading-relaxed"
          placeholder={t.rsvp.allergies.placeholder}
          value={person.allergies}
          onChange={(e) =>
            setPerson(kind, index, "allergies", e.target.value)
          }
          data-testid={`${testidPrefix}-allergies-${index}`}
        />
      </div>
    </motion.div>
  );
}

function PeopleStep({
  title,
  subtitle,
  kind,
  Icon,
  people,
  setPerson,
  addPerson,
  removePerson,
  t,
  testidPrefix,
  optional,
}) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="font-serif font-light text-2xl md:text-3xl text-ink mb-2">
          {title}
        </h3>
        <p className="font-serif italic text-ink/60 max-w-md mx-auto">
          {subtitle}
        </p>
      </div>

      <div className="space-y-5">
        <AnimatePresence>
          {people.map((p, i) => (
            <PersonCard
              key={`${kind}-${i}`}
              index={i}
              person={p}
              kind={kind}
              Icon={Icon}
              setPerson={setPerson}
              removePerson={removePerson}
              t={t}
              testidPrefix={testidPrefix}
            />
          ))}
        </AnimatePresence>
        {people.length === 0 && optional && (
          <p className="text-center text-sm font-serif italic text-ink/40 py-6">
            —
          </p>
        )}
      </div>

      <div className="text-center pt-2">
        <button
          type="button"
          onClick={() => addPerson(kind)}
          className="inline-flex items-center gap-2 px-6 py-2.5 border border-burgundy text-burgundy rounded-full text-xs uppercase tracking-[0.3em] hover:bg-burgundy hover:text-ivory transition-colors"
          data-testid={`${testidPrefix}-add`}
        >
          <Plus size={14} />
          {kind === "adults" ? t.rsvp.adults.add : t.rsvp.children.add}
        </button>
      </div>
    </div>
  );
}

function ContactStep({ form, update, t, testidPrefix }) {
  return (
    <div className="space-y-8">
      <div className="text-center space-y-2">
        <h3 className="font-serif font-light text-2xl md:text-3xl text-ink">
          {t.rsvp.contact.title}
        </h3>
        <p className="font-serif italic text-ink/60 text-sm">
          {t.rsvp.contact.hint}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="text-xs uppercase tracking-[0.3em] text-warm-brown mb-2 block">
            {t.rsvp.contact.email}
          </label>
          <input
            type="email"
            className="underline-input"
            value={form.email}
            onChange={(e) => update({ email: e.target.value })}
            data-testid={`${testidPrefix}-email-input`}
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.3em] text-warm-brown mb-2 block">
            {t.rsvp.contact.phone}
          </label>
          <input
            type="tel"
            className="underline-input"
            value={form.phone}
            onChange={(e) => update({ phone: e.target.value })}
            data-testid={`${testidPrefix}-phone-input`}
          />
        </div>
      </div>
    </div>
  );
}
